import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { Section, Button, Input, Select } from "@/components/ui/kit";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function DonorAdd({ mode }) {
  const nav = useNavigate();
  const [nats, setNats] = useState([]);
  const [busy, setBusy] = useState(false);
  
  const [form, setForm] = useState(mode === "individual" ? {
    salutation: "Mr.", fullName: "", gender: "MALE", dateOfBirth: "", nationalityId: "", 
    specialDateTitle: "", specialDate: "", 
    currentEmployer: "", referredBy: "", 
    email: "", primaryContactNo: "", whatsappNo: "", panNumber: "",
    addressLine1: "", addressLine2: "", city: "", district: "", state: "", country: "India", pincode: "",
    loginPassword: "donor123",
  } : {
    companyName: "", registeredOfficeAddress: "", 
    primaryContactName: "", primaryContactEmail: "", primaryContactPhone: "", primaryContactWhatsapp: "",
    secondaryContactName: "", secondaryContactEmail: "", secondaryContactPhone: "", secondaryContactWhatsapp: "",
    city: "", district: "", state: "", country: "India", pincode: "", panNumber: "",
    loginPassword: "donor123",
  });

  useEffect(() => {
    if (mode === "individual") {
      api.get("/master/nationalities").then(r => setNats(r.data.data));
    }
  }, [mode]);

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (mode === "individual") {
        payload.nationalityId = form.nationalityId ? Number(form.nationalityId) : null;
        if (!payload.specialDate) payload.specialDate = null;
        if (!payload.dateOfBirth) payload.dateOfBirth = null;
      }
      
      const r = await api.post(mode === "individual" ? "/donors/individual" : "/donors/corporate", payload);
      toast.success(`Donor created: ${r.data.data.donorCode}`);
      nav("/admin/donors");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create donor");
    } finally { 
      setBusy(false); 
    }
  };

  return (
    <form onSubmit={submit} className="space-y-8" data-testid={`donor-add-form-${mode}`}>
      <Section
        title={mode === "individual" ? "Add Individual Donor" : "Add Corporate Donor"}
        kicker="Donors"
        right={
          <Button type="submit" disabled={busy} data-testid="save-donor-btn">
            <Save className="w-4 h-4 mr-2"/> Save donor
          </Button>
        }
      />

      {mode === "individual" ? (
        <>
          <Panel title="Personal Information">
            <div className="flex gap-2 items-end sm:col-span-2">
              <div className="w-28 shrink-0">
                <Select label="Title" testid="fld-salutation" value={form.salutation} onChange={upd("salutation")}>
                  <option value="Mr.">Mr.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Dr.">Dr.</option>
                  <option value="Prof.">Prof.</option>
                  <option value="">None</option>
                </Select>
              </div>
              <div className="flex-1">
                <Input label="Full name" required testid="fld-fullName" value={form.fullName} onChange={upd("fullName")} />
              </div>
            </div>

            <Select label="Gender" testid="fld-gender" value={form.gender} onChange={upd("gender")}>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
              <option value="OTHER">Other</option>
            </Select>
            <Input label="Date of birth" type="date" testid="fld-dob" value={form.dateOfBirth} onChange={upd("dateOfBirth")} />
            
            <Select label="Nationality" testid="fld-nationality" value={form.nationalityId} onChange={upd("nationalityId")} required>
              <option value="">— select —</option>
              {nats.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
            </Select>

            <Input label="Special Date Title" placeholder="e.g. Anniversary" testid="fld-special-title" value={form.specialDateTitle} onChange={upd("specialDateTitle")} />
            <Input label="Special Date" type="date" testid="fld-special-date" value={form.specialDate} onChange={upd("specialDate")} />
          </Panel>
          
          <Panel title="Contact Details">
            <Input label="Email Address" type="email" testid="fld-email" value={form.email} onChange={upd("email")} />
            <Input label="Primary Contact No" testid="fld-contact" value={form.primaryContactNo} onChange={upd("primaryContactNo")}/>
            <Input label="WhatsApp No" testid="fld-whatsapp" value={form.whatsappNo} onChange={upd("whatsappNo")} />
            <Input label="PAN Number" testid="fld-pan" value={form.panNumber} onChange={upd("panNumber")} />
          </Panel>
          
          <Panel title="Address">
            <div className="sm:col-span-2 lg:col-span-4">
              <Input label="Address line 1" testid="fld-addr1" value={form.addressLine1} onChange={upd("addressLine1")} />
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <Input label="Address line 2" testid="fld-addr2" value={form.addressLine2} onChange={upd("addressLine2")} />
            </div>
            <Input label="City" testid="fld-city" value={form.city} onChange={upd("city")} />
            <Input label="District" testid="fld-district" value={form.district} onChange={upd("district")} />
            <Input label="State" testid="fld-state" value={form.state} onChange={upd("state")} />
            <Input label="Country" testid="fld-country" value={form.country} onChange={upd("country")} />
            <Input label="Pincode" testid="fld-pincode" value={form.pincode} onChange={upd("pincode")} />
          </Panel>
          
          <Panel title="Professional & Login">
            <Input label="Current Employer" testid="fld-employer" value={form.currentEmployer} onChange={upd("currentEmployer")} />
            <Input label="Referred by" testid="fld-referred" value={form.referredBy} onChange={upd("referredBy")} />
            <Input label="Portal Login Password" type="text" testid="fld-password" value={form.loginPassword} onChange={upd("loginPassword")} />
          </Panel>
        </>
      ) : (
        <>
          <Panel title="Company Details">
            <div className="sm:col-span-2">
              <Input label="Company Name" required testid="fld-companyName" value={form.companyName} onChange={upd("companyName")} />
            </div>
            <Input label="Company PAN Number" testid="fld-pan" value={form.panNumber} onChange={upd("panNumber")} />
          </Panel>
          
          <Panel title="Primary Contact">
            <Input label="Full Name"  testid="fld-primaryName" value={form.primaryContactName} onChange={upd("primaryContactName")} />
            <Input label="Email Address" type="email"  testid="fld-primaryEmail" value={form.primaryContactEmail} onChange={upd("primaryContactEmail")} />
            <Input label="Phone Number" testid="fld-primaryPhone" value={form.primaryContactPhone} onChange={upd("primaryContactPhone")} />
            <Input label="WhatsApp Number" testid="fld-primaryWhatsapp" value={form.primaryContactWhatsapp} onChange={upd("primaryContactWhatsapp")} />
          </Panel>

          <Panel title="Secondary Contact (Optional)">
            <Input label="Full Name" testid="fld-secondaryName" value={form.secondaryContactName} onChange={upd("secondaryContactName")} />
            <Input label="Email Address" type="email" testid="fld-secondaryEmail" value={form.secondaryContactEmail} onChange={upd("secondaryContactEmail")} />
            <Input label="Phone Number" testid="fld-secondaryPhone" value={form.secondaryContactPhone} onChange={upd("secondaryContactPhone")} />
            <Input label="WhatsApp Number" testid="fld-secondaryWhatsapp" value={form.secondaryContactWhatsapp} onChange={upd("secondaryContactWhatsapp")} />
          </Panel>

          <Panel title="Location & Login">
            <div className="sm:col-span-2 lg:col-span-4">
              <Input label="Registered Office Address" testid="fld-regAddr" value={form.registeredOfficeAddress} onChange={upd("registeredOfficeAddress")} />
            </div>
            <Input label="City" testid="fld-city" value={form.city} onChange={upd("city")} />
            <Input label="District" testid="fld-district" value={form.district} onChange={upd("district")} />
            <Input label="State" testid="fld-state" value={form.state} onChange={upd("state")} />
            <Input label="Country" testid="fld-country" value={form.country} onChange={upd("country")} />
            <Input label="Pincode" testid="fld-pincode" value={form.pincode} onChange={upd("pincode")} />
            <Input label="Portal Login Password" type="text" required testid="fld-password" value={form.loginPassword} onChange={upd("loginPassword")} />
          </Panel>
        </>
      )}
    </form>
  );
}

function Panel({ title, children }){
  return (
    <div className="rounded-xl border border-emerald-900/10 bg-white p-6 shadow-sm">
      <div className="overline mb-4 text-emerald-800 font-semibold uppercase tracking-wider text-sm">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {children}
      </div>
    </div>
  );
}